import { useEffect } from 'react';
import { signOut, useSession } from 'next-auth/react';
import { useRouter } from 'next/router';

interface SessionErrorHandlerProps {
  children: React.ReactNode;
}

const SessionErrorHandler = ({
  children,
}: SessionErrorHandlerProps): JSX.Element => {
  const { data: session, update } = useSession();
  const router = useRouter();

  useEffect(() => {
    // If refresh token has expired, sign out and redirect to sign in
    if (session?.error === 'RefreshTokenExpired') {
      console.info('Session expired, signing out...');
      signOut({ redirect: false });
    }
  }, [session, router]);

  // Listen for custom event to force session refresh with lock to prevent concurrent calls
  useEffect(() => {
    let isRefreshing = false;
    let refreshTimeout: NodeJS.Timeout | null = null;

    const handleForceSessionCheck = async () => {
      // If already refreshing, ignore
      if (isRefreshing) {
        console.info('Session refresh already in progress, skipping...');
        return;
      }

      // Clear any pending refresh timeout
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }

      // Debounce: wait 100ms before actually refreshing
      refreshTimeout = setTimeout(async () => {
        isRefreshing = true;
        console.info('Forcing session check due to API 401...');

        try {
          await update();
        } catch (error) {
          console.error('Error updating session:', error);
        } finally {
          // Reset lock after 2 seconds to allow retry if needed
          setTimeout(() => {
            isRefreshing = false;
          }, 2000);
        }
      }, 100);
    };

    window.addEventListener('forceSessionCheck', handleForceSessionCheck);
    return () => {
      window.removeEventListener('forceSessionCheck', handleForceSessionCheck);
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
    };
  }, [update]);

  return <>{children}</>;
};

export default SessionErrorHandler;
